import { useState, useCallback, createContext, useContext } from 'react';
import type React from 'react';
import { MessageFeedback } from '@dashai/types/message-feedback';
import { postWithAuthRetry, getLearningExtractorUrl } from '@dashai/lib/learningExtractorClient';
import { chatStoreClient } from '@dashai/lib/chatStoreClient';
import type { Message } from '@dashai/types/dashai';

interface MessageFeedbackContextValue {
  feedbackMap: Record<string, MessageFeedback>;
  submitFeedback: (
    messageId: string,
    chatId: string,
    type: 'positive' | 'negative',
    categories?: string[],
    comment?: string
  ) => Promise<void>;
  removeFeedback: (messageId: string) => Promise<void>;
  updateFeedbackDetails: (
    messageId: string,
    chatId: string,
    categories: string[],
    comment?: string
  ) => Promise<void>;
  triggerLearningExtraction: (
    messageId: string,
    chatId: string,
    categories: string[],
    comment?: string
  ) => void;
  loadFeedbackForChat: (chatId: string, userId: string) => Promise<void>;
  logCopyEvent: (messageId: string, chatId: string) => void;
}

export const MessageFeedbackContext = createContext<MessageFeedbackContextValue | null>(null);

export function useMessageFeedbackContext() {
  const ctx = useContext(MessageFeedbackContext);
  if (!ctx) {
    throw new Error('useMessageFeedbackContext must be used within MessageFeedbackContext.Provider');
  }
  return ctx;
}

export function useMessageFeedback(
  userId: string,
  clientId: string,
  messagesRef?: React.MutableRefObject<Message[]>,
) {
  const [feedbackMap, setFeedbackMap] = useState<Record<string, MessageFeedback>>({});

  const loadFeedbackForChat = useCallback(async (chatId: string, _uid: string) => {
    try {
      const { feedback } = await chatStoreClient.loadFeedback(chatId);
      const map: Record<string, MessageFeedback> = {};
      for (const row of feedback || []) {
        map[row.message_id] = {
          type: row.feedback_type as 'positive' | 'negative',
          categories: row.categories || [],
          comment: row.comment || undefined,
        };
      }
      setFeedbackMap(map);
    } catch (err) {
      console.error('Failed to load feedback:', err);
    }
  }, []);

  const submitFeedback = useCallback(async (
    messageId: string,
    chatId: string,
    type: 'positive' | 'negative',
    categories?: string[],
    comment?: string
  ) => {
    // Save previous value for revert
    const previous = feedbackMap[messageId];

    // Optimistic update
    setFeedbackMap(prev => ({
      ...prev,
      [messageId]: { type, categories: categories || [], comment },
    }));

    try {
      await chatStoreClient.submitFeedback(chatId, messageId, type, { categories, comment });
    } catch (err) {
      console.error('Failed to submit feedback:', err);
      // Revert to the previous vote, or to none
      setFeedbackMap(prev => {
        const next = { ...prev };
        if (previous) next[messageId] = previous;
        else delete next[messageId];
        return next;
      });
    }
  }, [feedbackMap]);

  const removeFeedback = useCallback(async (messageId: string) => {
    // Derive chatId from messagesRef; if not found, skip (matches old no-op behavior)
    const chatId = messagesRef?.current?.find((m) => m.id === messageId)?.chat_id as string | undefined;
    if (!chatId) return;

    // Save previous value for revert
    const previous = feedbackMap[messageId];

    // Optimistic update
    setFeedbackMap(prev => {
      const next = { ...prev };
      delete next[messageId];
      return next;
    });

    try {
      await chatStoreClient.removeFeedback(chatId, messageId);
    } catch (err) {
      console.error('Failed to remove feedback:', err);
      // Revert to previous value
      if (previous) {
        setFeedbackMap(prev => ({ ...prev, [messageId]: previous }));
      }
    }
  }, [feedbackMap, messagesRef]);

  const updateFeedbackDetails = useCallback(async (
    messageId: string,
    chatId: string,
    categories: string[],
    comment?: string
  ) => {
    // No-op if there's no existing feedback to amend.
    const existing = feedbackMap[messageId];
    if (!existing) return;

    // Optimistic update
    setFeedbackMap(prev => {
      const cur = prev[messageId];
      if (!cur) return prev;
      return { ...prev, [messageId]: { ...cur, categories, comment } };
    });

    // Re-submit with the existing type (server-side merge). Side effect lives
    // outside the state updater so it can't double-fire under StrictMode.
    try {
      await chatStoreClient.submitFeedback(chatId, messageId, existing.type, { categories, comment });
    } catch (err) {
      console.error('Failed to update feedback details:', err);
    }
  }, [feedbackMap]);

  const triggerLearningExtraction = useCallback((
    messageId: string,
    chatId: string,
    categories: string[],
    comment?: string,
  ) => {
    // Only extract when the user provides a comment — categories alone are too vague.
    if (!comment?.trim()) return;
    const url = getLearningExtractorUrl();
    if (!url) return;

    const transcript = (messagesRef?.current ?? []).map((m) => ({
      id: m.id, role: m.role, message: m.message,
    }));

    // In Firestore the feedback doc id IS the message_id, so pass it directly.
    postWithAuthRetry(url, {
      message_id: messageId,
      chat_id: chatId,
      user_id: userId,
      id_client: clientId,
      trigger_type: 'explicit_feedback',
      feedback_id: messageId,
      categories,
      comment: comment.trim(),
      messages: transcript,
    }).catch((err) => console.error('[feedback] Learning extraction failed:', err));
  }, [userId, clientId, messagesRef]);

  const logCopyEvent = useCallback(async (messageId: string, chatId: string) => {
    // Fire-and-forget analytics
    chatStoreClient.copyEvent(chatId, messageId)
      .catch((err) => console.error('Failed to log copy event:', err));
  }, []);

  return {
    feedbackMap,
    submitFeedback,
    removeFeedback,
    updateFeedbackDetails,
    triggerLearningExtraction,
    loadFeedbackForChat,
    logCopyEvent,
  };
}
